<template>
  <div
    class="price-medflex-list d-flex flex-column"
    :class="isMobile ? 'ga-6' : 'ga-8'"
    data-qa="lpu_services"
  >
    <PriceEmptyState
      v-if="isNotFound"
      :empty-src="emptySrc"
    />
    <template v-else>
      <div
        v-for="group in groups"
        :key="group.source.name"
        class="price-medflex-list__group d-flex flex-column"
        :class="isMobile ? 'ga-4' : 'ga-6'"
        data-qa="lpu_services_service_group"
      >
        <h2
          v-if="!hideGroupTitles"
          class="text--text"
          :class="isMobile ? 'text-h6' : 'text-h5'"
          data-qa="lpu_services_service_class_name_text"
        >
          {{ tDynamic(group.source, 'name') || group.source.name }}
        </h2>
        <PricesListCard list-qa="lpu_services_service_list">
          <PricesServiceRow
            v-for="(service, index) in group.services"
            :key="service.synonym_id"
            :service="service"
            :is-striped="index % 2 === 1"
            :is-calendar-open="openedSynonymId === service.synonym_id"
            :lpu-id="lpuId"
            :town-timedelta="townTimedelta"
            @open-calendar="onOpenCalendar"
            @close-calendar="openedSynonymId = null"
            @empty-schedule="onEmpty"
            @slot="onSlot"
          />
        </PricesListCard>
      </div>
      <div
        v-if="showAllButton"
        class="d-flex justify-center"
      >
        <v-btn
          depressed
          color="primary"
          height="52"
          :block="isMobile"
          class="branding-btn branding-btn--primary rounded-20 text-none text-subtitle-1"
          data-qa="lpu_services_service_price_show_all"
          @click="emit('show-all')"
        >
          {{ tStatic('Показать все услуги') }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import sendYaGoal from 'src/utils/sendYaGoal'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import unwrapList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/unwrapList'
import PricesListCard from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/components/PricesListCard.vue'
import PricesServiceRow from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/components/PricesServiceRow.vue'
import useServiceAppointment from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/composables/useServiceAppointment'
import type { SynonymPrice } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/types'
import mapMedflexService from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceCatalog/functions/mapMedflexService'
import { PRICE_PAGE_GOAL } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/constants'
import PriceEmptyState from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceCatalog/EmptyState.vue'
import type { MedflexServiceClass } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/types/medflex'

type Emits = {
  (_event: 'show-all'): void
}

const props = defineProps<{
  serviceClasses: MedflexServiceClass[]
  lpuId: number
  townTranslit: string
  townTimedelta: number
  isEnabledDirect: boolean
  isNotFound: boolean
  showAllButton: boolean
  emptySrc: string
  hideGroupTitles?: boolean
}>()

const emit = defineEmits<Emits>()
const isMobile = useIsMobile()
const openedSynonymId = ref<number | null>(null)
const { onEmptySchedule, onSlotClick } = useServiceAppointment({
  lpuId: props.lpuId,
  townTranslit: props.townTranslit,
  isEnabledDirect: props.isEnabledDirect,
})

const groups = computed(() => props.serviceClasses.map(item => ({
  source: item,
  services: unwrapList(item.sublist).map(service => (
    mapMedflexService(service)
  )),
})))

function onOpenCalendar(service: SynonymPrice) {
  sendYaGoal(PRICE_PAGE_GOAL.CLICK_PRICE)
  openedSynonymId.value = service.synonym_id
}

function onEmpty(service: SynonymPrice) {
  sendYaGoal(PRICE_PAGE_GOAL.CLICK_PRICE)
  onEmptySchedule(service)
}

function onSlot(service: SynonymPrice, date: string, time: string) {
  onSlotClick(service, date, time)
}
</script>

<style scoped lang="scss">
.price-medflex-list {
  width: 100%;
  min-width: 0;
  max-width: 100%;

  &__group {
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }
}
</style>
